import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { EditSubmitButton } from 'v2/common/EditSubmitButton/EditSubmitButton';
import {
  NotificationSettingsForm,
  NotificationSettingsSchema,
  TNotificationSettingsFormValues,
} from 'v2/common/NotificaitonSettingsForm/NotificationSettingsForm';
import { RootState, User } from 'v2/models';
import { getNotificationEditPerms } from 'v2/common/NotificaitonSettingsForm/getNotificationEditPerms';
import { useDispatch, useSelector } from 'react-redux';
import { getUserRole } from 'v2/utils/user';
import {
  getNotificationSettingsFormValues,
  getNotificationSettings,
} from 'v2/common/NotificaitonSettingsForm/getNotificationSettings';
import { toast } from 'react-toastify';
import { updateUserProfileAction } from 'actions';
import { yupResolver } from '@hookform/resolvers/yup';

export const SubscriptionNotifications = () => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isBusy, setIsBusy] = useState<boolean>(false);

  const notificationSettings = useSelector(
    (state: RootState) => state.user.user.notification_settings
  );
  const role = useSelector((state: RootState) => getUserRole(state.user.user));
  const permissions = useSelector(
    (state: RootState) => state.permissions.permissions
  );
  const { canEditNewInvoice } = getNotificationEditPerms(role, permissions);

  const formInitialValues =
    getNotificationSettingsFormValues(notificationSettings);

  const formProps = useForm<TNotificationSettingsFormValues>({
    defaultValues: formInitialValues,
    mode: 'onChange',
    resolver: yupResolver(NotificationSettingsSchema),
  });

  const dispatch = useDispatch();

  const {
    handleSubmit,
    formState: { isDirty },
    reset,
  } = formProps;
  const validSubmitHandler = async (
    formValues: TNotificationSettingsFormValues
  ) => {
    setIsBusy(true);
    try {
      if (isDirty) {
        const partialUser = {
          notification_settings: getNotificationSettings(formValues),
        };
        const response = (await updateUserProfileAction(partialUser)(
          dispatch
        )) as User;
        toast.success('Settings updated.');
        reset(
          getNotificationSettingsFormValues(response.notification_settings)
        );
      }

      setIsEditing(false);
      setIsBusy(false);
    } catch (error) {
      toast.error(error);
    }
    setIsBusy(false);
  };

  return canEditNewInvoice ? (
    <form onSubmit={handleSubmit(validSubmitHandler)}>
      <FormProvider {...formProps}>
        <NotificationSettingsForm
          disabled={!isEditing}
          showEmailOrg={false}
          showEmailUser={false}
          showEmailNewForReviewSearches={false}
          showEmailNewProviderResponseForReviewedRequests={false}
          showEmailNewInvoice={canEditNewInvoice}
        />

        <EditSubmitButton
          isBusy={isBusy}
          isEditing={isEditing}
          label="Edit details"
          onEdit={() => {
            setIsEditing(true);
          }}
        />
      </FormProvider>
    </form>
  ) : null;
};
